import React, { useState, useEffect, useRef, useCallback } from 'react';

function MathMonsters() {
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [monstersDefeated, setMonstersDefeated] = useState(0);
  const [level, setLevel] = useState(1);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [playerAnswer, setPlayerAnswer] = useState('');
  const [monsterHealth, setMonsterHealth] = useState(0);
  const [maxMonsterHealth, setMaxMonsterHealth] = useState(0);
  const [monsterImage, setMonsterImage] = useState('👹');
  const [message, setMessage] = useState('Ready to battle Math Monsters?');
  const [answerFeedback, setAnswerFeedback] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [difficulty, setDifficulty] = useState('hard');

  const correctSoundRef = useRef(null);
  const incorrectSoundRef = useRef(null);
  const monsterDefeatedSoundRef = useRef(null);
  const newProblemSoundRef = useRef(null);

  useEffect(() => {
    correctSoundRef.current = new Audio('https://www.soundjay.com/buttons/beep-07.mp3');
    incorrectSoundRef.current = new Audio('https://www.soundjay.com/misc/fail-buzzer-01.mp3');
    monsterDefeatedSoundRef.current = new Audio('https://www.soundjay.com/human/applause-5.mp3');
    newProblemSoundRef.current = new Audio('https://www.soundjay.com/buttons/button-1.mp3');
  }, []);

  const playSound = useCallback((soundRef) => {
    if (soundRef.current) {
      soundRef.current.currentTime = 0;
      soundRef.current.play().catch(e => console.error("Error playing sound:", e));
    }
  }, []);

  const generateProblem = useCallback((currentLevel, currentDifficulty) => {
    let maxNumBase, maxNumIncrement;
    if (currentDifficulty === 'easy') {
      maxNumBase = 5;
      maxNumIncrement = 2;
    } else if (currentDifficulty === 'hard') {
      maxNumBase = 5;
      maxNumIncrement = 5;
    } else {
      maxNumBase = 10;
      maxNumIncrement = 10;
    }

    const maxNum = maxNumBase + (currentLevel - 1) * maxNumIncrement;
    const num1 = Math.floor(Math.random() * maxNum) + 1;
    const num2 = Math.floor(Math.random() * maxNum) + 1;
    const operator = Math.random() < 0.5 ? '+' : '-';

    let problemAnswer, finalNum1 = num1, finalNum2 = num2;
    if (operator === '+') {
      problemAnswer = num1 + num2;
    } else {
      if (num1 < num2) {
        finalNum1 = num2;
        finalNum2 = num1;
      }
      problemAnswer = finalNum1 - finalNum2;
    }

    setCurrentProblem({ num1: finalNum1, num2: finalNum2, operator, answer: problemAnswer });
    setPlayerAnswer('');
    playSound(newProblemSoundRef);
  }, [playSound]);

  const spawnMonster = useCallback((currentLevel, currentDifficulty) => {
    let baseHealth, healthIncrement;
    if (currentDifficulty === 'easy') {
      baseHealth = 5;
      healthIncrement = 1;
    } else if (currentDifficulty === 'hard') {
      baseHealth = 10;
      healthIncrement = 3;
    } else {
      baseHealth = 15;
      healthIncrement = 5;
    }

    const health = baseHealth + (currentLevel - 1) * healthIncrement;
    setMaxMonsterHealth(health);
    setMonsterHealth(health);
    const monsters = ['👹', '👾', '👽', '👻', '👺', '🐲', '🐺', '🧟', '🐙', '🦖'];
    setMonsterImage(monsters[Math.floor(Math.random() * monsters.length)]);
    generateProblem(currentLevel, currentDifficulty);
  }, [generateProblem]);

  const startGame = useCallback(() => {
    setGameStarted(true);
    setScore(0);
    setMonstersDefeated(0);
    setLevel(1);
    setMessage('');
    setAnswerFeedback(null);
    setShowConfetti(false);
    spawnMonster(1, difficulty);
  }, [spawnMonster, difficulty]);

  const handleAnswerSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!currentProblem || !gameStarted) return;

      const parsedAnswer = parseInt(playerAnswer, 10);

      if (parsedAnswer === currentProblem.answer) {
        setScore((prevScore) => prevScore + 1);
        setMonsterHealth((prevHealth) => {
          const newHealth = prevHealth - 1;
          if (newHealth <= 0) {
            setMonstersDefeated((prevCount) => prevCount + 1);
            setMessage('Monster Defeated!');
            playSound(monsterDefeatedSoundRef);
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 3000);

            setTimeout(() => {
              setLevel((prevLevel) => prevLevel + 1);
              spawnMonster(level + 1, difficulty);
              setMessage('');
            }, 1500);
            return 0;
          }
          playSound(correctSoundRef);
          setAnswerFeedback('correct');
          setTimeout(() => setAnswerFeedback(null), 500);
          generateProblem(level, difficulty);
          return newHealth;
        });
      } else {
        setMessage('Incorrect answer!');
        playSound(incorrectSoundRef);
        setAnswerFeedback('incorrect');
        setTimeout(() => setAnswerFeedback(null), 500);
        setScore((prevScore) => Math.max(0, prevScore - 1));
      }
    },
    [playerAnswer, currentProblem, gameStarted, level, difficulty, generateProblem, spawnMonster, playSound],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-purple-400 to-cyan-400 flex items-center justify-center p-4 animate-gradient">
      <div className="bg-white bg-opacity-95 p-6 sm:p-8 md:p-12 rounded-3xl shadow-2xl max-w-lg w-full transform transition-all duration-300 hover:shadow-3xl backdrop-blur-lg border border-white/30">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-6 text-center animate-pulse-slow">
          Math Monsters
        </h1>

        {!gameStarted ? (
          <div className="text-center fade-in">
            <p className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">
              Solve math problems to defeat epic monsters!
            </p>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Select Difficulty:</h3>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 ${
                    difficulty === 'easy'
                      ? 'bg-emerald-500 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Easy
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 ${
                    difficulty === 'hard'
                      ? 'bg-amber-500 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Hard
                </button>
                <button
                  onClick={() => setDifficulty('tough')}
                  className={`px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 ${
                    difficulty === 'tough'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Tough
                </button>
              </div>
            </div>

            <button
              onClick={startGame}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xl rounded-full shadow-lg transform transition-all duration-300 hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-300 animate-bounce"
            >
              Begin Quest!
            </button>
          </div>
        ) : (
          <div className="fade-in">
            <div className="mb-6 flex flex-wrap justify-center gap-x-6 gap-y-4 text-lg sm:text-xl font-bold text-gray-800">
              <div className="flex items-center space-x-2">
                <span className="text-emerald-600">Score:</span>
                <span className="text-2xl font-extrabold text-emerald-700">{score}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">Level:</span>
                <span className="text-2xl font-extrabold text-blue-700">{level}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-purple-600">Defeated:</span>
                <span className="text-2xl font-extrabold text-purple-700">{monstersDefeated}</span>
              </div>
            </div>

            <div
              className={`text-xl sm:text-2xl font-bold mb-6 text-center transition-colors duration-300 ${
                answerFeedback === 'correct'
                  ? 'text-emerald-500'
                  : answerFeedback === 'incorrect'
                  ? 'text-rose-500'
                  : 'text-gray-800'
              }`}
            >
              {message || (currentProblem ? `${currentProblem.num1} ${currentProblem.operator} ${currentProblem.num2} = ?` : 'Loading...')}
            </div>

            <div className="relative w-48 h-48 sm:w-56 sm:h-56 mx-auto mb-6 flex items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-300 shadow-xl border-4 border-gray-200 transform transition-transform duration-300 hover:scale-105 monster-glow">
              <span className="text-8xl sm:text-9xl animate-wiggle">{monsterImage}</span>
              {maxMonsterHealth > 0 && monsterHealth > 0 && (
                <div className="absolute bottom-4 left-4 right-4 h-5 bg-gray-400 rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-rose-500 to-amber-500 transition-all duration-500 ease-out"
                    style={{ width: `${(monsterHealth / maxMonsterHealth) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>

            <form onSubmit={handleAnswerSubmit} className="flex flex-col items-center gap-4 w-full">
              <input
                type="number"
                value={playerAnswer}
                onChange={(e) => setPlayerAnswer(e.target.value)}
                className="w-full p-3 text-xl text-center border-2 border-purple-300 rounded-lg shadow-md focus:outline-none focus:ring-4 focus:ring-purple-400 transition-all duration-200 bg-white/80"
                placeholder="Your Answer"
                required
              />
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-lg rounded-full shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                Attack Monster!
              </button>
            </form>
          </div>
        )}

        {showConfetti && (
          <>
            {Array.from({ length: 100 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: `hsl(${Math.random() * 360}, 80%, 60%)`,
                  animationDelay: `${Math.random() * 3}s`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                  width: `${6 + Math.random() * 10}px`,
                  height: `${6 + Math.random() * 10}px`,
                }}
              />
            ))}
          </>
        )}

        <div className="mt-8 text-center max-w-md text-gray-700 text-sm">
          <h3 className="font-bold text-lg mb-2 text-gray-800">How to Play:</h3>
          <p>
            Brave adventurer, defeat Math Monsters by solving addition and subtraction problems! Each correct answer deals 1 damage. Defeat a monster to level up and face tougher challenges. Wrong answers cost 1 point, so be wise!
          </p>
        </div>
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;700;800&900&display=swap');

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 15s ease infinite;
        }

        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-pulse-slow {
          animation: pulseSlow 3s ease-in-out;
        }

        @keyframes pulseSlow {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        .animate-bounce {
          animation: bounce 1.5s infinite;
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .fade-in {
          animation: fadeIn 0.8s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }

        @keyframes wiggle {
          0%, 100% {
            transform: rotate(-3deg);
          }
          50% {
            transform: rotate(3deg);
          }
        }

        .confetti-piece {
          position: fixed;
          border-radius: 50%;
          opacity: 0;
          animation: fall 4s ease-out forwards;
          z-index: 100;
        }

        @keyframes fall {
          0% {
            opacity: 0.9;
            transform: translateY(-100vh) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(720deg);
          }
        }

        .monster-glow {
          filter: drop-shadow(0 0 15px rgba(255, 107, 107, 0.6));
        }

        .shadow-glow {
          box-shadow: 0 0 20px rgba(255,255,255,0.5), 0 0 30px rgba(255,107,107,0.3);
        }
      `}</style>
    </div>
  );
}

export default MathMonsters;